import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  OnDestroy,
  ViewChild,
  ElementRef
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import {
  NbCardModule,
  NbInputModule,
  NbSpinnerModule,
  NbSelectModule,
  NbOptionModule,
  NbCheckboxModule,
  NbIconModule
} from "@nebular/theme";
import { TranslateModule } from "@ngx-translate/core";
import { KPI } from "../../../../@core/api";
import {
  AssetsService,
  QuantityUnitsService,
  ScopeService,
  ConfigService,
  AssetsChangesService,
  SaveToastService
} from "../../../../@core/utils";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NbDialogService } from "@nebular/theme";
import { RightPanelDialogService } from "../../../../@core/utils/right-panel-dialog.service";
import { AssetPhotoCarouselDialogComponent } from "../../asset-photo-carousel-dialog/asset-photo-carousel-dialog.component";
import { AssetTypeSelectorPageComponent } from "../../asset-type-selector/asset-type-selector-page.component";
import { AssetTypeWithHierarchy } from "../../asset-type-selector/asset-type-selector.component";
import { first, Subscription } from "rxjs";

@Component({
  selector: "app-descriptif-tab",
  templateUrl: "./descriptif-tab.component.html",
  styleUrls: ["./descriptif-tab.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSpinnerModule,
    NbInputModule,
    NbSelectModule,
    NbOptionModule,
    NbCheckboxModule,
    NbIconModule,
    FontAwesomeModule,
    TranslateModule,
    AssetPhotoCarouselDialogComponent
  ]
})
export class DescriptifTabComponent implements OnChanges, OnDestroy {
  @Input() public asset: KPI.MappedAsset;
  @Input() public loading: boolean = false;
  @Input() public canGoToAudit: boolean = false;
  @Input() public equipmentValidationEnabled: boolean = false;
  @Output() public formChange = new EventEmitter<{ form: UntypedFormGroup; initialValues: any }>();
  @Output() public assetDataChanged = new EventEmitter<void>();
  @Output() public saveRequested = new EventEmitter<void>();

  public form: UntypedFormGroup;
  public initialValues: any;
  public quantityUnitName: string = "";
  public assetTypeHierarchy: string = "";
  public assetPictures: KPI.AssetPicture[] = [];
  public failedPictureIds: Set<number> = new Set();
  private loadedPicturesForAssetId: number | null = null;
  public showUniformatCode: boolean = false;
  public showBrand: boolean = false;
  public showModel: boolean = false;
  public showMaintenanceCode: boolean = false;
  public isUploadingPhoto: boolean = false;
  private subscriptions: Subscription[] = [];
  private _pendingCounter: number = 0;

  @ViewChild("photoFileInput") private photoFileInput: ElementRef<HTMLInputElement>;

  constructor(
    private assetsService: AssetsService,
    private quantityUnitsService: QuantityUnitsService,
    private scopeService: ScopeService,
    private cdr: ChangeDetectorRef,
    private dialogService: NbDialogService,
    private configService: ConfigService,
    private assetsChanges: AssetsChangesService,
    private rightPanelDialogService: RightPanelDialogService,
    private saveToast: SaveToastService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public onFieldSaveRequested(): void {
    this.saveRequested.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["asset"] && this.asset) {
      this.initializeForm();
      this.loadQuantityUnit();
      this.buildAssetTypeHierarchy();
      this.loadAssetPictures();
      this.loadAssetTypeDetails();
      this.loadConfiguration();
    }
  }

  private initializeForm(): void {
    this.initialValues = {
      label: this.asset.label || "",
      quantity: this.asset.quantity || 0,
      uniformat_code: (this.asset as any).uniformat_code || "",
      brand: this.asset.brand || "",
      model: this.asset.model || "",
      maintenance_code: this.asset.maintenance_code || "",
      zone_details: this.asset.zone_details || "",
      comments: this.asset.comments || ""
    };

    this.form = new UntypedFormGroup({
      label: new UntypedFormControl({ value: this.initialValues.label, disabled: false }),
      quantity: new UntypedFormControl({ value: this.initialValues.quantity, disabled: false }, [
        Validators.required,
        Validators.min(1)
      ]),
      uniformat_code: new UntypedFormControl({ value: this.initialValues.uniformat_code, disabled: false }),
      brand: new UntypedFormControl({ value: this.initialValues.brand, disabled: false }),
      model: new UntypedFormControl({ value: this.initialValues.model, disabled: false }),
      maintenance_code: new UntypedFormControl({ value: this.initialValues.maintenance_code, disabled: false }),
      zone_details: new UntypedFormControl({ value: this.initialValues.zone_details, disabled: false }),
      comments: new UntypedFormControl({ value: this.initialValues.comments, disabled: false }, [
        Validators.maxLength(512)
      ])
    });

    this.subscriptions.push(
      this.form.valueChanges.subscribe(() => {
        this.formChange.emit({ form: this.form, initialValues: this.initialValues });
      })
    );

    this.formChange.emit({ form: this.form, initialValues: this.initialValues });
  }

  private loadQuantityUnit(): void {
    this.subscriptions.push(
      this.quantityUnitsService.quantityUnits$.pipe(first()).subscribe({
        next: quantityUnits => {
          const assetUnit = quantityUnits.find(
            quantityUnit => quantityUnit.id === this.asset?.asset_type_object?.quantity_unit
          );
          this.quantityUnitName = assetUnit ? assetUnit.name : "u";
          this.cdr.detectChanges();
        },
        error: () => {
          this.quantityUnitName = "u";
          this.cdr.detectChanges();
        }
      })
    );
  }

  private buildAssetTypeHierarchy(): void {
    if (this.asset) {
      const parts = [];
      if (this.asset.super_category?.name) parts.push(this.asset.super_category.name);
      if (this.asset.category?.name) parts.push(this.asset.category.name);
      if (this.asset.sub_category?.name) parts.push(this.asset.sub_category.name);
      if (this.asset.asset_type_object?.name) parts.push(this.asset.asset_type_object.name);
      this.assetTypeHierarchy = parts.join(" > ");
    }
  }

  private loadAssetPictures(): void {
    if (this.loadedPicturesForAssetId === this.asset.id) {
      return;
    }
    this.loadedPicturesForAssetId = this.asset.id;
    this.subscriptions.push(
      this.assetsService.getAssetsPictures([this.asset.id]).subscribe({
        next: pictures => {
          this.assetPictures = pictures;
          this.assetDataChanged.emit();
          this.cdr.detectChanges();
        },
        error: () => {
          this.assetPictures = [];
          this.assetDataChanged.emit();
          this.cdr.detectChanges();
        }
      })
    );
  }

  private loadAssetTypeDetails(): void {
    this.subscriptions.push(
      this.scopeService.assetTypes$.pipe(first()).subscribe(assetTypes => {
        const assetTypeDetails = assetTypes[this.asset.asset_type];

        if (assetTypeDetails) {
          this.showBrand = assetTypeDetails.brand_support || false;
          this.showModel = assetTypeDetails.model_support || false;
          this.showMaintenanceCode = assetTypeDetails.show_maintenance_code || false;
        }

        this.assetDataChanged.emit();
        this.cdr.detectChanges();
      })
    );
  }

  private loadConfiguration(): void {
    this.subscriptions.push(
      this.configService.customerConfig$.pipe(first()).subscribe(config => {
        this.showUniformatCode = config?.uniformat_code_display_in_equipment_sheet || false;
        this.assetDataChanged.emit();
        this.cdr.detectChanges();
      })
    );
  }

  public getThumbnailUrl(picture: KPI.AssetPicture): string | null {
    if (!picture?.thumbnail) {
      return null;
    }
    let thumbnail = picture.thumbnail.trim();
    thumbnail = thumbnail.replace(/["']/g, '');
    return thumbnail || null;
  }

  public onImageError(event: any, picture: KPI.AssetPicture): void {
    const cleanedThumbnail = this.getThumbnailUrl(picture);
    if (event.target.src === cleanedThumbnail && picture.picture !== cleanedThumbnail) {
      event.target.src = picture.picture;
    } else {
      this.failedPictureIds.add(picture.id);
      this.cdr.detectChanges();
    }
  }

  public triggerPhotoUpload(): void {
    this.photoFileInput?.nativeElement.click();
  }

  private resizeImage(file: File, maxWidth = 720, maxHeight = 1080): Promise<Blob> {
    return new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => resolve(blob ?? file), file.type, 0.85);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  }

  public onPhotoFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    this.isUploadingPhoto = true;

    const localId = `${Date.now()}-${(++this._pendingCounter).toString(36)}`;
    const localUrl = URL.createObjectURL(file);
    const optimisticPicture: KPI.AssetPicture = { id: localId as any, picture: localUrl, thumbnail: null, asset: this.asset.id } as any;
    this.assetPictures = [...this.assetPictures, optimisticPicture];
    this.cdr.detectChanges();

    this.resizeImage(file).then(blob => {
      const resized = new File([blob], file.name, { type: file.type });
      const formData = new FormData();
      formData.append("picture", resized);
      formData.append("asset", String(this.asset.id));
      formData.append("local_id", localId);

      this.subscriptions.push(
        this.assetsService.createAssetPicture(formData).subscribe({
          next: newPicture => {
            URL.revokeObjectURL(localUrl);
            this.assetPictures = this.assetPictures.map(p => (p.id as any) === localId ? newPicture : p);
            this.isUploadingPhoto = false;
            if (this.photoFileInput) {
              this.photoFileInput.nativeElement.value = "";
            }
            this.assetDataChanged.emit();
            this.cdr.detectChanges();
          },
          error: () => {
            URL.revokeObjectURL(localUrl);
            this.assetPictures = this.assetPictures.filter(p => (p.id as any) !== localId);
            this.isUploadingPhoto = false;
            this.cdr.detectChanges();
          }
        })
      );
    });
  }

  public openPhotoCarousel(initialIndex: number = 0): void {
    const dialogRef = this.dialogService.open(AssetPhotoCarouselDialogComponent, {
      autoFocus: false,
      hasBackdrop: true,
      closeOnBackdropClick: true,
      backdropClass: 'carousel-transparent-backdrop',
      hasScroll: false,
      context: {
        asset: this.asset,
        pictures: this.assetPictures,
        initialIndex
      } as any
    });

    this.subscriptions.push(
      dialogRef.onClose.subscribe((updatedPictures: KPI.AssetPicture[]) => {
        if (Array.isArray(updatedPictures)) {
          const changed = updatedPictures.length !== this.assetPictures.length;
          this.assetPictures = updatedPictures;
          if (changed) {
            this.assetDataChanged.emit();
          }
          this.cdr.detectChanges();
        } else {
          this.loadedPicturesForAssetId = null;
          this.loadAssetPictures();
        }
      })
    );
  }

  public canChangeAssetType(): boolean {
    return true;
  }

  public openAssetTypeSelector(): void {
    if (!this.canChangeAssetType()) {
      return;
    }
    const dialog = this.rightPanelDialogService.open(AssetTypeSelectorPageComponent, {
      autoFocus: false,
      closeOnBackdropClick: true,
      hasScroll: false,
      context: {
        parent: this.asset?.parent ? this.asset : null,
        autoSelectAssetTypeId: this.asset?.asset_type || 0,
        autoSelectSubCategoryId: this.asset?.sub_category?.id || 0,
        autoSelectCategoryId: this.asset?.category?.id || 0,
        preventAssetTypesThatNeedParent: !!this.asset?.parent
      }
    });

    dialog.onClose.subscribe((selectedAssetType: AssetTypeWithHierarchy) => {
      if (selectedAssetType && this.asset) {
        this.saveToast.showSaving();
        this.assetsChanges.addChange(this.asset.id, "asset_type", selectedAssetType.id);

        this.assetTypeHierarchy = [
          selectedAssetType.super_category?.name,
          selectedAssetType.category?.name,
          selectedAssetType.sub_category?.name,
          selectedAssetType.name
        ]
          .filter(Boolean)
          .join(" > ");

        this.asset.asset_type = selectedAssetType.id;
        if (selectedAssetType.super_category) {
          this.asset.super_category = selectedAssetType.super_category;
        }
        if (selectedAssetType.category) {
          this.asset.category = selectedAssetType.category;
        }
        if (selectedAssetType.sub_category) {
          this.asset.sub_category = selectedAssetType.sub_category;
        }
        this.asset.asset_type_object = selectedAssetType;
        this.showMaintenanceCode = selectedAssetType.show_maintenance_code || false;

        this.subscriptions.push(
          this.assetsChanges.pendingChangesNumber$.pipe(first(nb => nb === 0)).subscribe(() => {
            this.saveToast.showSaved();
            this.assetsService
              .getAsset(this.asset.id)
              .pipe(first())
              .subscribe(updatedAsset => {
                if (updatedAsset) {
                  this.asset = { ...updatedAsset as any } as KPI.MappedAsset;
                  this.buildAssetTypeHierarchy();
                  this.loadQuantityUnit();
                  this.loadAssetTypeDetails();
                  this.cdr.detectChanges();
                }
              });
          })
        );

        this.cdr.detectChanges();
      }
    });
  }

public getPerimeterDisplay(): string {
    const perimeters = this.asset?.perimeters;
    if (!perimeters?.length) return '';

    const siteName = this.asset.site_name || perimeters[0]?.name || '';
    const lastPerimeter = perimeters[perimeters.length - 1];

    if (lastPerimeter.level_parent) {
      const parentPerimeter =
        perimeters.find(p => p.id === lastPerimeter.level_parent) ||
        perimeters[0]?.sub_perimeters?.find(sp => sp.id === lastPerimeter.level_parent);

      if (parentPerimeter) {
        return `${siteName} > ${parentPerimeter.name} > ${lastPerimeter.name}`;
      }

      return `${siteName} > ${lastPerimeter.name}`;
    }

    return perimeters.map(p => p.name).join(' > ');
  }

  public isFieldMissing(_fieldName: string): boolean {
    return false;
  }

  public goToAudit(): void {
    this.assetsService.openAuditAssetEditor({
      asset: this.asset,
      siteId: this.asset.perimeters[0].id,
      tab: 'expert-notations'
    });
  }
}
